import { useMemo } from "react";

interface ResumeItem {
  id: string;
  [key: string]: any;
  summary: string;
}

interface ResumeSection {
  id: string;
  name: string;
  items: ResumeItem[];
  [key: string]: any;
}

type DiffSections = {
  [sectionKey: string]: ResumeSection;
};
export interface DiffResumeData {
  sections: DiffSections;
}

interface ComparisonResult {
  selector: string;
  type: "modified" | "added" | "removed";
  oldValue: any;
  newValue: any;
}

const getDiffs = (original: DiffSections, optimized: DiffSections) => {
  const results: ComparisonResult[] = [];

  // Get all section keys from both objects
  const allSectionKeys = new Set([...Object.keys(original), ...Object.keys(optimized)]);

  for (const sectionKey of allSectionKeys) {
    const originalSection = original[sectionKey];
    const optimizedSection = optimized[sectionKey];

    // Skip if section doesn't have items array
    if (!originalSection?.items && !optimizedSection?.items) {
      continue;
    }

    const originalItems = originalSection?.items || [];
    const optimizedItems = optimizedSection?.items || [];
    const sectionName = originalSection?.id || optimizedSection?.id || sectionKey;

    // Create maps for quick lookup by id
    const originalItemsMap = new Map(
      originalItems.map((item, index) => [item.id, { item, index }]),
    );

    // Track processed items to identify removed ones
    const processedIds = new Set<string>();

    // Check for added and modified items
    optimizedItems.forEach((optimizedItem, optimizedIndex) => {
      processedIds.add(optimizedItem.id);
      const originalData = originalItemsMap.get(optimizedItem.id);

      if (!originalData) {
        // Item was added
        results.push({
          selector: `#${sectionName} .section-items > div:nth-child(${optimizedIndex + 1})`,
          type: "added",
          oldValue: "",
          newValue: optimizedItem,
        });
      } else {
        // Item exists, check if modified
        const originalItem = originalData.item;
        const originalIndex = originalData.index;

        if (JSON.stringify(originalItem) !== JSON.stringify(optimizedItem)) {
          results.push({
            selector: `#${sectionName} .section-items > div:nth-child(${originalIndex + 1})`,
            type: "modified",
            oldValue: originalItem,
            newValue: optimizedItem,
          });
        }
      }
    });

    // Check for removed items
    originalItems.forEach((originalItem, originalIndex) => {
      if (!processedIds.has(originalItem.id)) {
        // Item was removed - use original index
        results.push({
          selector: `#${sectionName} .section-items > div:nth-child(${originalIndex + 1})`,
          type: "removed",
          oldValue: originalItem,
          newValue: "",
        });
      }
    });
  }

  return results;
};

export default function useDiff(initial: DiffResumeData, updated: DiffResumeData) {
  return useMemo(() => {
    if (!initial || !updated) return [];

    const sectionDiff = getDiffs(initial.sections, updated.sections);

    return sectionDiff;
  }, [initial, updated]);
}
